import { CommentStatus } from '@/@types/comment'
import type { ContentType } from '@/@types/common'
import type { CommentNotification as CommentNotificationType } from '@/@types/notification'
import ClickableStackAvatarGroup from '@/components/ClickableStackAvatarGroup'
import { CONTENT_LOCALE } from '@/constants/locale'
import { formatAsRelativeTime } from '@/utils/day'
import { getCommentQueryOptions } from '@/utils/fetchers/comment'
import useNotificationClickHandler from '@/utils/hooks/useNotificationClickHandler'
import queryClient from '@/utils/queryClient'
import { modals } from '@mantine/modals'
import { AxiosError } from 'axios'
import clsx from 'clsx'
import { MouseEvent, type HTMLAttributes } from 'react'
import toast from 'react-hot-toast'
import { showCommentFormModal } from '../Comment/utils'
import ContentContainer from '../ContentContainer'
import HoverableText from '../HoverableText'
import ViewPictureText from '../Picture/ViewPictureText'
import ScreenName from '../ScreenName'
import styles from './CommentNotification.module.css'
import NotificationContainer from './NotificationContainer'
import NotificationReference from './NotificationReference'

export interface CommentNotificationProps extends HTMLAttributes<HTMLDivElement> {
	data: CommentNotificationType
	type: ContentType
	noReplyButton?: boolean
}

export default function CommentNotification({
	data,
	type: contentType,
	noReplyButton = false,
	className,
	...props
}: CommentNotificationProps) {
	const {
		actionItem: { users, usersCount, content, status, pictures },
		referenceItem,
		createdAt,
	} = data
	const isDeleted = status === CommentStatus.DELETED
	const { handleClick, isClickable } = useNotificationClickHandler(data)

	const handleReplyButtonClick = async (e: MouseEvent<HTMLElement, globalThis.MouseEvent>) => {
		e.stopPropagation()
		const [comment] = await Promise.all([
			queryClient.ensureQueryData(
				getCommentQueryOptions({
					id: data.actionItem.id,
					targetType: data.actionItem.targetType,
				})
			),
		]).catch((err: AxiosError<{ error: string }>) => {
			if (err.response?.data?.error) {
				toast.error(err.response.data.error)
			}
			throw err
		})

		const modalId = showCommentFormModal({
			replyToComment: {
				to: comment,
				threadId: comment.threadId,
			},
			onClose: () => {
				modals.close(modalId)
			},
		})
	}

	return (
		<>
			<NotificationContainer
				onClick={handleClick}
				isClickable={isClickable}
				className={clsx(styles.container, className)}
				{...props}
			>
				<div className={styles.content}>
					<div className={styles.mainRow}>
						<ClickableStackAvatarGroup data={users} />

						<div className={styles.contentColumn}>
							<header className={styles.header}>
								<div className={styles.headerText}>
									<ScreenName data={users[0]} className={styles.screenName} />
									<span className={styles.secondaryText}>
										{contentType === 'COMMENT' ? '回复' : '评论'}了你的{CONTENT_LOCALE[contentType]}
									</span>
								</div>
							</header>

							<main className={styles.main}>
								<ContentContainer isDeleted={isDeleted} className={styles.contentContainer}>
									{isDeleted ? (
										'该评论已删除'
									) : (
										<>
											{content}
											{pictures && pictures.length > 0 && <ViewPictureText data={pictures[0]} />}
										</>
									)}
								</ContentContainer>
							</main>
						</div>
					</div>
					<footer className={styles.footer}>
						<span>{formatAsRelativeTime(createdAt)}</span>
						{!noReplyButton && !isDeleted && (
							<HoverableText onClick={handleReplyButtonClick}>回复</HoverableText>
						)}
					</footer>
				</div>

				<NotificationReference data={referenceItem} />
			</NotificationContainer>
		</>
	)
}
